import {
	lazy,
	Suspense,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import {
	selectCanInsertPathIntoMessage,
	selectEffectiveEnvironmentId,
	findGroupWithFile,
	type EditorGroup,
	type EditorLayoutNode,
	type EditorSplitDirection,
	useChatStore,
} from "@/stores/useChatStore";
import { toMinSizePercent, usePanelGroupSize } from "@/lib/panel-min-size";
import { environmentDisplayPath } from "@/lib/shared-volume-files";
import { copyPathToClipboard, insertPathIntoMessage } from "@/lib/path-actions";
import { useDragStore } from "@/stores/useDragStore";
import { useLayoutStore } from "@/stores/useLayoutStore";
import { useDiagnosticsStore } from "@/stores/useDiagnosticsStore";
import { IconButton } from "@/components/common/IconButton";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ContextMenu, ContextMenuPosition } from "@/components/common/ContextMenu";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Icon } from "@/components/common/Icon";
import { environmentFileContentUrl } from "@/lib/api";
import { collectOpenEnvironmentFiles } from "@/lib/environment-files";
import { isKnowledgeArticleFileId } from "@/lib/knowledge-graph";
import {
	getPreviewType,
	MAX_DOCX_PREVIEW_BYTES,
	MAX_EDITABLE_BYTES,
	MAX_TABLE_PREVIEW_BYTES,
} from "@/lib/file-type";
import { resolveTabDropIndex } from "@/lib/dnd/file-tab-drop";
import { useDraggable } from "@/lib/dnd/useDraggable";
import { useDropTarget } from "@/lib/dnd/useDropTarget";
import { resolveEditorDropZone, type EditorDropZone } from "@/lib/dnd/editor-drop-zone";
import type { FileEditorHandle } from "@/components/preview/FileEditor";
import { FileHeaderBar } from "@/components/preview/FileHeaderBar";
import { PreviewSkeleton } from "@/components/preview/PreviewSkeleton";
import { ImagePreview } from "@/components/preview/ImagePreview";
import { VideoPreview } from "@/components/preview/VideoPreview";
import { MarkdownPreview } from "@/components/preview/MarkdownPreview";
import { HtmlPreview } from "@/components/preview/HtmlPreview";
import { SvgPreview } from "@/components/preview/SvgPreview";
import { CsvPreview } from "@/components/preview/CsvPreview";
import { PdfPreview } from "@/components/preview/PdfPreview";
import { UnsupportedFilePreview } from "@/components/preview/UnsupportedFilePreview";
import { CLOSE_ACTIVE_FILE_EVENT } from "@/lib/shortcut-defs";
import { problemIndicatorFor } from "@/lib/problems";
import { tabLabelStyle } from "@/components/common/tab-shell";
import {
	fileTabBodyStyle,
	fileTabFlareStyle,
	fileTabInactiveClassName,
	fileTabShellStyle,
} from "@/components/preview/file-tab-shell";

// エディタ一式 (Monaco 本体 + worker) を初期バンドルから分離するため、
// ファイルタブを実際に開くまで読み込まない。
const FileEditor = lazy(() =>
	import("@/components/preview/FileEditor").then((m) => ({ default: m.FileEditor })),
);

// docx-preview (と依存の jszip) は Word ファイルを開くまで読み込まない。
const DocxPreview = lazy(() =>
	import("@/components/preview/DocxPreview").then((m) => ({ default: m.DocxPreview })),
);

// SheetJS (xlsx) は Excel ファイルを開くまで読み込まない。
const ExcelPreview = lazy(() =>
	import("@/components/preview/ExcelPreview").then((m) => ({ default: m.ExcelPreview })),
);

/** グループ内の index 番目のタブが、target を残す「他のタブをすべて閉じる」で閉じられるか。 */
function closableWith(group: EditorGroup, id: string, index: number, target: string): boolean {
	return index >= group.pinnedCount && id !== target;
}

export function RightPane() {
	const environmentId = useChatStore(selectEffectiveEnvironmentId);
	const environmentFileChildren = useChatStore((s) => s.environmentFileChildren);
	const openFileEntries = useChatStore((s) => s.openFileEntries);
	const activeFileId = useChatStore((s) => s.activeFileId);
	const editorGroups = useChatStore((s) => s.editorGroups);
	const editorLayout = useChatStore((s) => s.editorLayout);
	const activeEditorGroupId = useChatStore((s) => s.activeEditorGroupId);
	const dirtyFileIds = useChatStore((s) => s.dirtyFileIds);
	const openFile = useChatStore((s) => s.openFile);
	const openFilePreview = useChatStore((s) => s.openFilePreview);
	const closeFile = useChatStore((s) => s.closeFile);
	const closeOtherFiles = useChatStore((s) => s.closeOtherFiles);
	const splitEditor = useChatStore((s) => s.splitEditor);
	const unsplitEditors = useChatStore((s) => s.unsplitEditors);
	const selectEditorGroup = useChatStore((s) => s.selectEditorGroup);
	const moveFileToGroup = useChatStore((s) => s.moveFileToGroup);
	const splitEditorWithFile = useChatStore((s) => s.splitEditorWithFile);
	const pinFile = useChatStore((s) => s.pinFile);
	const unpinFile = useChatStore((s) => s.unpinFile);
	const navigateToEnvironmentFilesTreeNode = useChatStore(
		(s) => s.navigateToEnvironmentFilesTreeNode,
	);
	const canInsertPathIntoMessage = useChatStore(selectCanInsertPathIntoMessage);
	const [menuState, setMenuState] = useState<{
		position: ContextMenuPosition;
		fileId: string;
	} | null>(null);
	const [pendingClose, setPendingClose] = useState<
		{ kind: "closeFile"; fileId: string } | { kind: "closeOtherFiles"; fileId: string } | null
	>(null);

	// 未保存の変更があるタブを閉じようとしたら確認を挟む。dirty でなければ即座に閉じる。
	const requestCloseFile = useCallback(
		(fileId: string) => {
			if (dirtyFileIds.includes(fileId)) {
				setPendingClose({ kind: "closeFile", fileId });
			} else {
				closeFile(fileId);
			}
		},
		[dirtyFileIds, closeFile],
	);

	const requestCloseOtherFiles = useCallback(
		(fileId: string) => {
			// 実際に閉じられるのは対象ファイルと同じグループ内の、ピン留めも常設タブでもない対象外タブのみ
			// (他グループには触れない)。
			const targetGroup = findGroupWithFile(editorGroups, fileId);
			const closingDirty = targetGroup
				? targetGroup.fileIds.some(
						(id, i) => closableWith(targetGroup, id, i, fileId) && dirtyFileIds.includes(id),
					)
				: false;
			if (closingDirty) {
				setPendingClose({ kind: "closeOtherFiles", fileId });
			} else {
				closeOtherFiles(fileId);
			}
		},
		[dirtyFileIds, editorGroups, closeOtherFiles],
	);

	useEffect(() => {
		const closeActiveFile = () => {
			if (activeFileId) requestCloseFile(activeFileId);
		};
		window.addEventListener(CLOSE_ACTIVE_FILE_EVENT, closeActiveFile);
		return () => window.removeEventListener(CLOSE_ACTIVE_FILE_EVENT, closeActiveFile);
	}, [activeFileId, requestCloseFile]);

	// 未保存の変更がある間はページ離脱 (リロード・タブを閉じる) を確認させる。
	useEffect(() => {
		if (dirtyFileIds.length === 0) return;
		const handler = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [dirtyFileIds.length]);

	const openFileIds = useChatStore((s) => s.openFileIds);
	const filePathById = useMemo(() => {
		const files = collectOpenEnvironmentFiles(
			environmentId,
			environmentFileChildren,
			openFileEntries,
			openFileIds,
		);
		return new Map([...files.values()].map((file) => [file.id, file.path]));
	}, [environmentId, environmentFileChildren, openFileEntries, openFileIds]);
	const activeFilePath = activeFileId ? filePathById.get(activeFileId) : undefined;

	useEffect(() => {
		// 写しのファイル (絶対パス) はホームのツリーに無い。
		if (activeFilePath && !activeFilePath.startsWith("/"))
			navigateToEnvironmentFilesTreeNode(activeFilePath);
	}, [activeFilePath, navigateToEnvironmentFilesTreeNode]);

	const menuFilePath = menuState ? filePathById.get(menuState.fileId) : undefined;
	const menuFileGroup = menuState ? findGroupWithFile(editorGroups, menuState.fileId) : undefined;
	const menuFilePinned =
		menuState && menuFileGroup
			? menuFileGroup.fileIds.indexOf(menuState.fileId) < menuFileGroup.pinnedCount
			: false;
	const handleCopyPath = useCallback(async () => {
		if (!menuFilePath) return;
		await copyPathToClipboard(environmentDisplayPath(menuFilePath));
	}, [menuFilePath]);

	const handleAddPathToInput = useCallback(() => {
		if (!menuFilePath) return;
		insertPathIntoMessage(environmentDisplayPath(menuFilePath));
	}, [menuFilePath]);

	return (
		<div
			data-testid="right-pane"
			style={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				minHeight: 0,
				background: "var(--surface-2)",
			}}
		>
			{menuState && (
				<ContextMenu
					position={menuState.position}
					onClose={() => setMenuState(null)}
					testId="preview-tab-menu"
					items={[
						...(isKnowledgeArticleFileId(menuState.fileId)
							? []
							: [
									{
										label: "パスをコピー",
										testId: "preview-tab-menu-copy-path",
										onSelect: () => handleCopyPath(),
									},
									...(canInsertPathIntoMessage
										? [
												{
													label: "メッセージにパスを追加",
													testId: "preview-tab-menu-add-path",
													onSelect: () => handleAddPathToInput(),
												},
											]
										: []),
									{ separator: true as const },
								]),
						menuFilePinned
							? {
									label: "ピン留めを解除",
									testId: "preview-tab-menu-unpin",
									onSelect: () => unpinFile(menuState.fileId),
								}
							: {
									label: "ピン留め",
									testId: "preview-tab-menu-pin",
									onSelect: () => pinFile(menuState.fileId),
								},
						{
							label: "閉じる",
							testId: "preview-tab-menu-close",
							onSelect: () => requestCloseFile(menuState.fileId),
						},
						...(menuFileGroup?.fileIds.some(
							(id, i) => i >= menuFileGroup.pinnedCount && id !== menuState.fileId,
						)
							? [
									{
										label: "他のタブをすべて閉じる",
										testId: "preview-tab-menu-close-others",
										onSelect: () => requestCloseOtherFiles(menuState.fileId),
									},
								]
							: []),
					]}
				/>
			)}
			<ConfirmDialog
				open={pendingClose !== null}
				title="未保存の変更を破棄"
				message="保存せずにタブを閉じると、変更内容は失われます。破棄しますか？"
				confirmLabel="破棄する"
				onConfirm={async () => {
					if (pendingClose?.kind === "closeFile") closeFile(pendingClose.fileId);
					if (pendingClose?.kind === "closeOtherFiles") closeOtherFiles(pendingClose.fileId);
					setPendingClose(null);
				}}
				onCancel={() => setPendingClose(null)}
			/>
			<div style={{ flex: 1, minHeight: 0, background: "var(--surface-1)" }}>
				<EditorWorkspace
					layout={editorLayout}
					groups={editorGroups}
					activeGroupId={activeEditorGroupId}
					filePathById={filePathById}
					dirtyFileIds={dirtyFileIds}
					onSelectGroup={selectEditorGroup}
					onOpenFilePreview={openFilePreview}
					onPromoteFile={openFile}
					onCloseFile={requestCloseFile}
					onUnpinFile={unpinFile}
					onContextMenu={(event, fileId) => {
						event.preventDefault();
						setMenuState({
							position: { x: event.clientX, y: event.clientY, align: "left" },
							fileId,
						});
					}}
					onSplit={splitEditor}
					onUnsplit={unsplitEditors}
					onMoveFileToGroup={moveFileToGroup}
					onSplitEditorWithFile={splitEditorWithFile}
				/>
			</div>
		</div>
	);
}

interface FileTabProps {
	fileId: string;
	groupId: string;
	index: number;
	pinned: boolean;
	label: string;
	/** ワークスペース相対パス。診断 (エラー・警告) のファイル名着色に使う。 */
	path?: string;
	title?: string;
	/** グループで表示中のファイルのタブか。 */
	shown: boolean;
	/** タブのグループがフォーカス中か。 */
	focused: boolean;
	preview: boolean;
	dirty: boolean;
	onSelect: (fileId: string) => void;
	/** ダブルクリックでプレビュータブを常設タブへ昇格させる。 */
	onPromote: (fileId: string) => void;
	onClose: (fileId: string) => void;
	onUnpin: (fileId: string) => void;
	onContextMenu: (e: React.MouseEvent) => void;
}

/** ドロップ座標からタブの左半分/右半分どちら側かを判定する。 */
function dropSideOf(e: React.DragEvent): "before" | "after" {
	const rect = e.currentTarget.getBoundingClientRect();
	return e.clientX < rect.left + rect.width / 2 ? "before" : "after";
}

/**
 * ドラッグされたファイルタブを targetGroupId 内のタブ index の side 側へ移動する。ドラッグ元が
 * 既に targetGroupId に属するなら同グループ内の並べ替え (moveOpenFile)、別グループなら移動
 * (moveFileToGroup) として扱う。ドラッグ元が見つからない (未オープン) 場合は no-op。
 */
function moveDraggedTab(
	fileId: string,
	targetGroupId: string,
	index: number,
	side: "before" | "after",
	moveOpenFile: (fileId: string, toIndex: number) => void,
	moveFileToGroup: (fileId: string, targetGroupId: string, index?: number) => void,
) {
	const sourceGroup = findGroupWithFile(useChatStore.getState().editorGroups, fileId);
	if (!sourceGroup) return;
	if (sourceGroup.id === targetGroupId) {
		const sourceIndex = sourceGroup.fileIds.indexOf(fileId);
		moveOpenFile(fileId, resolveTabDropIndex(sourceIndex, index, side));
	} else {
		moveFileToGroup(fileId, targetGroupId, side === "before" ? index : index + 1);
	}
}

function FileTab({
	fileId,
	groupId,
	index,
	pinned,
	label,
	path,
	title,
	shown,
	focused,
	preview,
	dirty,
	onSelect,
	onPromote,
	onClose,
	onUnpin,
	onContextMenu,
}: FileTabProps) {
	const [hoverClose, setHoverClose] = useState(false);
	const [dropSide, setDropSide] = useState<"before" | "after" | null>(null);
	const moveOpenFile = useChatStore((s) => s.moveOpenFile);
	const moveFileToGroup = useChatStore((s) => s.moveFileToGroup);
	const diagnostics = useDiagnosticsStore((s) => (path ? s.byPath[path] : undefined));
	const { total: totalProblems, color: problemColor } = problemIndicatorFor(diagnostics);

	const dragProps = useDraggable({ kind: "file-tab", fileId, name: label });
	const { isOver, dropProps } = useDropTarget({
		accept: "file-tab",
		// グループを問わず、自分自身へのドロップ以外は受け入れる (他グループからのドラッグはタブ移動として扱う)。
		canDrop: (p) => p.fileId !== fileId,
		onDragOverAccepted: (_p, e) => setDropSide(dropSideOf(e)),
		onDrop: (p, e) => {
			const side = dropSideOf(e);
			moveDraggedTab(p.fileId, groupId, index, side, moveOpenFile, moveFileToGroup);
		},
	});

	return (
		<div
			title={title}
			data-tooltip-nowrap
			data-testid={`preview-tab-file-${fileId}`}
			data-active={shown && focused ? "true" : "false"}
			data-preview={preview ? "true" : "false"}
			data-pinned={pinned ? "true" : "false"}
			onClick={() => onSelect(fileId)}
			onDoubleClick={() => onPromote(fileId)}
			onContextMenu={onContextMenu}
			onAuxClick={(e) => {
				if (e.button === 1 && !pinned) onClose(fileId);
			}}
			{...dragProps}
			{...dropProps}
			onDragLeave={() => {
				dropProps.onDragLeave();
				setDropSide(null);
			}}
			className={shown ? undefined : fileTabInactiveClassName}
			style={fileTabShellStyle(shown, focused)}
		>
			{isOver && dropSide && (
				<span
					data-testid={`preview-tab-file-${fileId}-drop-indicator`}
					data-side={dropSide}
					style={{
						position: "absolute",
						top: 0,
						bottom: 0,
						left: dropSide === "before" ? -1 : undefined,
						right: dropSide === "after" ? -1 : undefined,
						width: 2,
						background: "var(--accent-line)",
					}}
				/>
			)}
			{shown && (
				<>
					<span style={fileTabFlareStyle("left")} />
					<span style={fileTabFlareStyle("right")} />
				</>
			)}
			<span
				style={{
					...tabLabelStyle,
					fontStyle: preview ? "italic" : "normal",
					// 斜体は字面が右にはみ出すため、overflow: hidden の切り取りに掛からない余白を足す。
					paddingRight: preview ? 2 : 0,
					color: problemColor,
				}}
			>
				{label}
			</span>
			{totalProblems > 0 && (
				<span
					style={{
						flexShrink: 0,
						fontFamily: "var(--font-mono)",
						fontSize: "var(--text-section)",
						color: problemColor,
					}}
				>
					{totalProblems}
				</span>
			)}
			<button
				type="button"
				data-tooltip={
					pinned ? "ピン留めを解除" : dirty ? "未保存の変更があります (閉じる)" : "閉じる"
				}
				data-testid={`preview-tab-file-${fileId}-close`}
				data-dirty={dirty ? "true" : "false"}
				onClick={(e) => {
					e.stopPropagation();
					if (pinned) onUnpin(fileId);
					else onClose(fileId);
				}}
				style={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					width: 16,
					height: 16,
					padding: 0,
					border: "none",
					borderRadius: "var(--radius-2)",
					background: "transparent",
					color: "inherit",
					cursor: "pointer",
					flexShrink: 0,
				}}
				onMouseEnter={(e) => {
					e.currentTarget.style.background = "var(--overlay-active)";
					setHoverClose(true);
				}}
				onMouseLeave={(e) => {
					e.currentTarget.style.background = "transparent";
					setHoverClose(false);
				}}
			>
				{dirty && !hoverClose ? (
					<span
						style={{
							width: 7,
							height: 7,
							borderRadius: "var(--radius-full)",
							background: "var(--fg-1)",
							display: "inline-block",
						}}
					/>
				) : pinned ? (
					<Icon name="pin" size={10} />
				) : (
					<Icon name="x" size={10} />
				)}
			</button>
		</div>
	);
}

/** EditorWorkspace/EditorGroupPane が共通して受け取る、タブ操作のコールバック一式。 */
interface EditorPaneCallbacks {
	filePathById: Map<string, string>;
	dirtyFileIds: string[];
	onSelectGroup: (groupId: string) => void;
	onOpenFilePreview: (fileId: string) => void;
	/** ダブルクリックでプレビュータブを常設タブへ昇格させる。 */
	onPromoteFile: (fileId: string) => void;
	onCloseFile: (fileId: string) => void;
	onUnpinFile: (fileId: string) => void;
	onContextMenu: (event: React.MouseEvent, fileId: string) => void;
	onSplit: (direction: EditorSplitDirection) => void;
	onUnsplit: () => void;
	onMoveFileToGroup: (fileId: string, groupId: string, index?: number) => void;
	onSplitEditorWithFile: (groupId: string, direction: EditorSplitDirection, fileId: string) => void;
}

interface EditorWorkspaceProps extends EditorPaneCallbacks {
	layout: EditorLayoutNode;
	groups: EditorGroup[];
	activeGroupId: string;
}

/**
 * ファイル内容の描画先となる安定ホスト要素を、この位置へ DOM 移動で載せる受け皿。
 * useLayoutEffect なので旧位置からの除去と同一タスク内で再挿入され、再生中の動画が
 * 「文書から外れた」と観測して停止することはない。
 */
function EditorContentSlot({ host }: { host: HTMLDivElement }) {
	const ref = useRef<HTMLDivElement>(null);
	useLayoutEffect(() => {
		ref.current?.appendChild(host);
	}, [host]);
	return <div ref={ref} style={{ height: "100%", minWidth: 0, minHeight: 0 }} />;
}

/**
 * 入れ子の PanelGroup でエディタの左右・上下分割を描画する。
 *
 * ファイル内容 (PreviewTabContent) はツリー位置ではなくファイル id に紐づく安定ホスト要素へ
 * portal で描画する。分割・解除でレイアウト木の形が変わったりタブが別グループへ移ったりすると、
 * ツリー位置ベースの描画では React が内容ごと再マウントしてしまい、再生中の動画の読み直しや
 * エディタ状態の消失を招くため。
 */
function EditorWorkspace({
	layout,
	groups,
	activeGroupId,
	filePathById,
	dirtyFileIds,
	onSelectGroup,
	onOpenFilePreview,
	onPromoteFile,
	onCloseFile,
	onUnpinFile,
	onContextMenu,
	onSplit,
	onUnsplit,
	onMoveFileToGroup,
	onSplitEditorWithFile,
}: EditorWorkspaceProps) {
	const groupsById = useMemo(() => new Map(groups.map((group) => [group.id, group])), [groups]);
	const contentHostsRef = useRef(new Map<string, HTMLDivElement>());
	const contentHostFor = useCallback((fileId: string): HTMLDivElement => {
		const hosts = contentHostsRef.current;
		let host = hosts.get(fileId);
		if (!host) {
			host = document.createElement("div");
			host.style.height = "100%";
			host.style.minWidth = "0";
			host.style.minHeight = "0";
			hosts.set(fileId, host);
		}
		return host;
	}, []);

	useEffect(() => {
		const openFileIds = new Set(groups.flatMap((group) => group.fileIds));
		for (const fileId of contentHostsRef.current.keys()) {
			if (!openFileIds.has(fileId)) contentHostsRef.current.delete(fileId);
		}
	}, [groups]);

	const pinToggleGroupId = topRightGroupId(layout);
	const renderNode = (node: EditorLayoutNode): React.ReactNode => {
		if (node.kind === "group") {
			const group = groupsById.get(node.groupId);
			if (!group) return null;
			return (
				<EditorGroupPane
					group={group}
					active={group.id === activeGroupId}
					showPinToggle={group.id === pinToggleGroupId}
					canUnsplit={groups.length > 1}
					contentHostFor={contentHostFor}
					filePathById={filePathById}
					dirtyFileIds={dirtyFileIds}
					onSelectGroup={onSelectGroup}
					onOpenFilePreview={onOpenFilePreview}
					onPromoteFile={onPromoteFile}
					onCloseFile={onCloseFile}
					onUnpinFile={onUnpinFile}
					onContextMenu={onContextMenu}
					onSplit={onSplit}
					onUnsplit={onUnsplit}
					onMoveFileToGroup={onMoveFileToGroup}
					onSplitEditorWithFile={onSplitEditorWithFile}
				/>
			);
		}
		return (
			<EditorSplitPanels
				direction={node.direction}
				first={renderNode(node.first)}
				second={renderNode(node.second)}
			/>
		);
	};

	return (
		<div style={{ height: "100%", minHeight: 0 }}>
			{renderNode(layout)}
			{groups.flatMap((group) =>
				group.fileIds.map((fileId) =>
					createPortal(
						<PreviewTabContent
							previewFileId={fileId}
							active={group.id === activeGroupId && group.activeFileId === fileId}
						/>,
						contentHostFor(fileId),
						fileId,
					),
				),
			)}
		</div>
	);
}

/** 分割の右上にあたるグループ。右ペイン全体の操作 (ピン留め) はここのタブ列に置く。 */
function topRightGroupId(node: EditorLayoutNode): string {
	if (node.kind === "group") return node.groupId;
	return topRightGroupId(node.direction === "horizontal" ? node.second : node.first);
}

/** 分割したエディタ 1 枚の最小幅・最小高 (px)。タブ列と本文が読める大きさ。 */
const EDITOR_SPLIT_MIN_WIDTH = 200;
const EDITOR_SPLIT_MIN_HEIGHT = 120;

/** グループの実寸を測り終えるまで使う下限 (%)。初回描画の 1 フレームだけ効く。 */
const EDITOR_SPLIT_MIN_SIZE_FALLBACK = 15;

/**
 * エディタ 1 段分の分割。下限を % で指定すると入れ子のたびに親比で掛かり、数回分割
 * しただけで中身が読めない幅まで潰せてしまうため、実寸から px の下限へ換算する。
 */
function EditorSplitPanels({
	direction,
	first,
	second,
}: {
	direction: "horizontal" | "vertical";
	first: React.ReactNode;
	second: React.ReactNode;
}) {
	const { ref, size } = usePanelGroupSize(direction);
	const minSize = toMinSizePercent(
		direction === "horizontal" ? EDITOR_SPLIT_MIN_WIDTH : EDITOR_SPLIT_MIN_HEIGHT,
		size,
		EDITOR_SPLIT_MIN_SIZE_FALLBACK,
	);
	const resizeClass =
		direction === "horizontal" ? "izanagi-resize-handle" : "izanagi-resize-handle-row";

	return (
		<div ref={ref} style={{ height: "100%", minWidth: 0, minHeight: 0 }}>
			<PanelGroup direction={direction} style={{ minWidth: 0, minHeight: 0 }}>
				<Panel defaultSize={50} minSize={minSize}>
					{first}
				</Panel>
				<PanelResizeHandle className={resizeClass} />
				<Panel defaultSize={50} minSize={minSize}>
					{second}
				</Panel>
			</PanelGroup>
		</div>
	);
}

/** ヘッダー (タブ列) の高さ (px)。この帯の上へのドロップは常に center 扱いにする。 */
const EDITOR_GROUP_HEADER_HEIGHT = 32;

const EDITOR_DROP_ZONE_TO_SPLIT_DIRECTION: Record<
	Exclude<EditorDropZone, "center">,
	EditorSplitDirection
> = { left: "left", right: "right", top: "up", bottom: "down" };

/** ドロップ座標 (DragEvent) から、ペイン内のどのゾーンに該当するかを判定する。判定自体は resolveEditorDropZone (純関数) に委譲する。 */
function resolveEditorDropZoneFromEvent(e: React.DragEvent): EditorDropZone {
	const rect = e.currentTarget.getBoundingClientRect();
	return resolveEditorDropZone(
		e.clientX - rect.left,
		e.clientY - rect.top,
		rect.width,
		rect.height,
		EDITOR_GROUP_HEADER_HEIGHT,
	);
}

/** 分割先ゾーンをペイン内のハイライト領域のスタイルへ変換する。center はペイン全体を薄く示す。 */
function editorDropZoneStyle(zone: EditorDropZone): React.CSSProperties {
	const base: React.CSSProperties = {
		position: "absolute",
		pointerEvents: "none",
		background: "var(--row-dragover-bg)",
		zIndex: 10,
	};
	switch (zone) {
		case "left":
			// ヘッダーを除いたコンテンツ領域を、ドロップ後に実際に分割される半分の大きさで示す。
			return { ...base, left: 0, top: EDITOR_GROUP_HEADER_HEIGHT, bottom: 0, width: "50%" };
		case "right":
			return { ...base, right: 0, top: EDITOR_GROUP_HEADER_HEIGHT, bottom: 0, width: "50%" };
		case "top":
			return {
				...base,
				left: 0,
				right: 0,
				top: EDITOR_GROUP_HEADER_HEIGHT,
				height: `calc((100% - ${EDITOR_GROUP_HEADER_HEIGHT}px) / 2)`,
			};
		case "bottom":
			return { ...base, left: 0, right: 0, bottom: 0, height: "50%" };
		case "center":
			// ヘッダー (タブ列) を除いたコンテンツ領域のみを、分割を示す半面より薄く敷いて区別する。
			return {
				...base,
				left: 0,
				right: 0,
				top: EDITOR_GROUP_HEADER_HEIGHT,
				bottom: 0,
				background: "var(--pane-dragover-bg)",
			};
	}
}

interface EditorGroupPaneProps extends EditorPaneCallbacks {
	group: EditorGroup;
	active: boolean;
	/** 右ペインのピン留めボタンを置くか (右上のグループだけ)。 */
	showPinToggle: boolean;
	canUnsplit: boolean;
	/** ファイル内容が portal で描画されている安定ホスト要素を返す。EditorWorkspace が所有する。 */
	contentHostFor: (fileId: string) => HTMLDivElement;
}

function EditorGroupPane({
	group,
	active,
	showPinToggle,
	canUnsplit,
	contentHostFor,
	filePathById,
	dirtyFileIds,
	onSelectGroup,
	onOpenFilePreview,
	onPromoteFile,
	onCloseFile,
	onUnpinFile,
	onContextMenu,
	onSplit,
	onUnsplit,
	onMoveFileToGroup,
	onSplitEditorWithFile,
}: EditorGroupPaneProps) {
	const [dropZone, setDropZone] = useState<EditorDropZone | null>(null);
	const isDraggingFileTab = useDragStore((s) => s.payload?.kind === "file-tab");
	const { isOver, dropProps } = useDropTarget({
		accept: "file-tab",
		canDrop: () => true,
		onDragOverAccepted: (_payload, e) => setDropZone(resolveEditorDropZoneFromEvent(e)),
		onDrop: (payload, e) => {
			const zone = resolveEditorDropZoneFromEvent(e);
			if (zone === "center") onMoveFileToGroup(payload.fileId, group.id);
			else
				onSplitEditorWithFile(group.id, EDITOR_DROP_ZONE_TO_SPLIT_DIRECTION[zone], payload.fileId);
		},
	});

	// タブ帯 (個々の FileTab の隙間・末尾の空白) へのドロップは、タブの上へのドロップと同じ
	// 「タブ移動」として扱う (ペイン中央へ移すコンテンツ領域の挙動とは区別する)。末尾への
	// 挿入として扱うため、常に最後のタブの after 側とみなす。
	const moveOpenFile = useChatStore((s) => s.moveOpenFile);
	const { isOver: isTabRowOver, dropProps: tabRowDropProps } = useDropTarget({
		accept: "file-tab",
		canDrop: () => true,
		onDrop: (payload) => {
			moveDraggedTab(
				payload.fileId,
				group.id,
				group.fileIds.length,
				"after",
				moveOpenFile,
				onMoveFileToGroup,
			);
		},
	});

	return (
		<div
			data-testid={`editor-group-${group.id}`}
			onMouseDown={() => onSelectGroup(group.id)}
			{...dropProps}
			onDragLeave={() => {
				dropProps.onDragLeave();
				setDropZone(null);
			}}
			style={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				minHeight: 0,
				background: "var(--surface-1)",
				position: "relative",
			}}
		>
			{isOver && dropZone && (
				<div
					data-testid={`editor-group-${group.id}-drop-${dropZone}`}
					style={editorDropZoneStyle(dropZone)}
				/>
			)}
			<div
				className="pane-header"
				style={{
					minHeight: EDITOR_GROUP_HEADER_HEIGHT,
					gap: 0,
					padding: 0,
					overflow: "hidden",
					background: "var(--surface-2)",
					// タブ帯は下線を引かず、アクティブなタブが本文と同じ背景色で繋がる。
					borderBottom: "none",
				}}
			>
				<div
					{...tabRowDropProps}
					className="tab-header-scroll"
					style={{
						display: "flex",
						flex: 1,
						minWidth: 0,
						alignSelf: "stretch",
						overflowX: "auto",
						overflowY: "hidden",
					}}
				>
					{group.fileIds.map((fileId, index) => {
						const path = filePathById.get(fileId);
						return (
							<FileTab
								key={fileId}
								fileId={fileId}
								groupId={group.id}
								index={index}
								pinned={index < group.pinnedCount}
								label={path?.split("/").pop() ?? fileId}
								path={path}
								title={path}
								shown={group.activeFileId === fileId}
								focused={active}
								preview={group.previewFileId === fileId}
								dirty={dirtyFileIds.includes(fileId)}
								onSelect={onOpenFilePreview}
								onPromote={onPromoteFile}
								onClose={onCloseFile}
								onUnpin={onUnpinFile}
								onContextMenu={(event) => onContextMenu(event, fileId)}
							/>
						);
					})}
					{isTabRowOver && (
						// 絶対配置ではなく通常のフローに乗せることで、末尾タブのすぐ右側 (帯の余白の
						// 位置に関わらず) に固定して表示する。
						<span
							data-testid={`editor-group-${group.id}-tab-row-drop-indicator`}
							style={{
								flexShrink: 0,
								alignSelf: "stretch",
								width: 2,
								background: "var(--accent-line)",
							}}
						/>
					)}
				</div>
				{(active || showPinToggle) && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							flexShrink: 0,
							gap: 1,
							padding: "0 4px",
						}}
					>
						{active && (
							<>
								<IconButton
									icon="splitRight"
									iconSize={14}
									size={22}
									title="右に分割"
									data-testid={`editor-group-${group.id}-split-right`}
									onClick={() => onSplit("right")}
								/>
								{canUnsplit && (
									<IconButton
										icon="unsplit"
										iconSize={14}
										size={22}
										title="分割を解除"
										data-testid={`editor-group-${group.id}-unsplit`}
										onClick={onUnsplit}
									/>
								)}
							</>
						)}
						{showPinToggle && <RightPanePinToggle />}
					</div>
				)}
			</div>
			<div
				style={{
					flex: 1,
					minHeight: 0,
					position: "relative",
					...fileTabBodyStyle,
				}}
			>
				{isDraggingFileTab && (
					// portal 描画のファイル内容と iframe は drag イベントをこのペインへ届けないため受け皿を重ねる。
					<div
						data-testid={`editor-group-${group.id}-drop-overlay`}
						style={{ position: "absolute", inset: 0, zIndex: 10 }}
					/>
				)}
				{group.fileIds.length === 0 ? (
					<div
						style={{
							display: "flex",
							height: "100%",
							alignItems: "center",
							justifyContent: "center",
							padding: 16,
							color: "var(--fg-3)",
							fontSize: "var(--text-caption)",
						}}
					>
						ファイルを選択するとここに表示されます
					</div>
				) : (
					group.fileIds.map((fileId) => (
						<div
							key={fileId}
							style={{
								height: "100%",
								display: group.activeFileId === fileId ? "block" : "none",
								// ドラッグ中だけ Monaco のウィジェット (z-index 35〜) を受け皿より下に閉じ込める。
								isolation: isDraggingFileTab ? "isolate" : undefined,
							}}
						>
							<EditorContentSlot host={contentHostFor(fileId)} />
						</div>
					))
				)}
			</div>
		</div>
	);
}

/** 右ペインのピン留め (ファイルがなくても自動で閉じない) を切り替える。 */
function RightPanePinToggle() {
	const pinned = useLayoutStore((s) => s.rightPanePinned);
	const togglePinned = useLayoutStore((s) => s.toggleRightPanePinned);
	return (
		<IconButton
			icon="pin"
			iconSize={14}
			size={22}
			color={pinned ? "var(--accent)" : undefined}
			background={pinned ? "var(--accent-subtle)" : undefined}
			hoverBackground={pinned ? "var(--accent-subtle)" : undefined}
			activeBackground={pinned ? "var(--accent-subtle)" : undefined}
			data-tooltip={
				pinned
					? "ピン留めを解除する (ファイルがないときは右ペインを自動で閉じる)"
					: "右ペインをピン留めする (ファイルがなくても閉じない)"
			}
			data-testid="right-pane-pin-toggle"
			onClick={togglePinned}
		/>
	);
}

/** 写しのファイルのパンくずは、ホームのツリーに無いので辿らない。 */
function ignoreNavigation(): void {}

function PreviewTabContent({
	previewFileId,
	active,
}: {
	previewFileId: string | null;
	active: boolean;
}) {
	const environmentId = useChatStore(selectEffectiveEnvironmentId);
	const environmentFileChildren = useChatStore((s) => s.environmentFileChildren);
	const openFileEntries = useChatStore((s) => s.openFileEntries);
	const setFileDirty = useChatStore((s) => s.setFileDirty);
	const navigateToEnvironmentFilesTreeNode = useChatStore(
		(s) => s.navigateToEnvironmentFilesTreeNode,
	);
	const dirty = useChatStore((s) =>
		previewFileId ? s.dirtyFileIds.includes(previewFileId) : false,
	);
	const previewFile = useMemo(
		() =>
			previewFileId === null
				? undefined
				: collectOpenEnvironmentFiles(environmentId, environmentFileChildren, openFileEntries, [
						previewFileId,
					]).get(previewFileId),
		[environmentId, environmentFileChildren, openFileEntries, previewFileId],
	);
	const editorRef = useRef<FileEditorHandle>(null);
	const pendingReveal = useChatStore((s) =>
		previewFileId ? s.pendingReveals[previewFileId] : undefined,
	);
	const [viewMode, setViewMode] = useState<"preview" | "source">("preview");
	const [previewSource, setPreviewSource] = useState<string | null>(null);
	const [embeddedDiffOpen, setEmbeddedDiffOpen] = useState(false);
	const [externalChangeBefore, setExternalChangeBefore] = useState<string | null>(null);
	const [confirmDiscard, setConfirmDiscard] = useState(false);
	const [saving, setSaving] = useState(false);
	// 差分表示を開く直前の viewMode。差分を閉じたときに元の表示へ戻すために覚えておく。
	const viewModeBeforeDiffRef = useRef<"preview" | "source" | null>(null);

	// 検索結果からの行ジャンプ要求がこのタブ宛てなら、既に開いているタブでもソース表示へ
	// 切り替える (マウント時の初期状態だけでなく、開いた後の再要求にも反応させる)。
	useEffect(() => {
		if (!pendingReveal) return;
		viewModeBeforeDiffRef.current = null;
		setViewMode("source");
	}, [pendingReveal]);

	const filename = previewFile?.path.split("/").pop() ?? "プレビュー";
	const previewType = getPreviewType(filename);

	// ファイル本体は backend がサンドボックスから直接ストリーミングするため、URL は決定的に組み立てられる
	// (取得のためのラウンドトリップは不要)。updatedAt を version に乗せて内容更新時にキャッシュを無効化する。
	// インライン表示用 (contentUrl) とダウンロード用 (downloadUrl, Content-Disposition: attachment 付き) は
	// 常に同時に確定するため1つの useMemo にまとめる。
	const previewUrls = useMemo(() => {
		if (!environmentId || !previewFile?.id) return null;
		const version = previewFile.updatedAt;
		return {
			contentUrl: environmentFileContentUrl(environmentId, previewFile.id, { version }),
			downloadUrl: environmentFileContentUrl(environmentId, previewFile.id, {
				download: true,
				version,
			}),
		};
	}, [environmentId, previewFile?.id, previewFile?.updatedAt]);
	const withinSizeLimit = previewFile != null && previewFile.sizeBytes <= MAX_EDITABLE_BYTES;
	const withinDocxSizeLimit =
		previewFile != null && previewFile.sizeBytes <= MAX_DOCX_PREVIEW_BYTES;
	const withinTableSizeLimit =
		previewFile != null && previewFile.sizeBytes <= MAX_TABLE_PREVIEW_BYTES;
	// 共有ボリュームの写しのファイルは読み取り専用。HTML はホームの外を配信できないので、ソースだけを出す。
	const readOnly = previewFile?.readOnly === true;
	const isSourceViewer = previewType === "other" || (readOnly && previewType === "html");
	const canEdit = withinSizeLimit && !readOnly;
	const isMarkdownEditable = previewType === "markdown" && canEdit;
	const isCsvEditable = previewType === "csv" && canEdit;
	const isHtmlEditable = previewType === "html" && canEdit;
	const isSvgEditable = previewType === "svg" && canEdit;
	// プレビュー/ソース切替を持つ編集可能ファイル (markdown・csv・html・svg)。
	const isToggleEditable = isMarkdownEditable || isCsvEditable || isHtmlEditable || isSvgEditable;
	// テキストかどうかは FileEditor が本体を読み込むときに判定する (事前の Range リクエストは張らない)。
	const isCodeEditable = isSourceViewer && withinSizeLimit;
	const editable = isCodeEditable || isToggleEditable;
	const sourceVisible = isCodeEditable || viewMode === "source";
	const csvDelimiter = filename.toLowerCase().endsWith(".tsv") ? "\t" : ",";
	const enterDiffView = useCallback(() => {
		if (!isToggleEditable) return;
		setViewMode((current) => {
			if (viewModeBeforeDiffRef.current === null) viewModeBeforeDiffRef.current = current;
			return "source";
		});
	}, [isToggleEditable]);
	const handleDiffOpenChange = useCallback(
		(open: boolean) => {
			setEmbeddedDiffOpen(open);
			if (open) {
				enterDiffView();
				return;
			}
			if (viewModeBeforeDiffRef.current !== null) {
				setViewMode(viewModeBeforeDiffRef.current);
				viewModeBeforeDiffRef.current = null;
			}
		},
		[enterDiffView],
	);
	// .txt → .md のように種別が変わるリネームでも、編集を続けられるようソース表示のままにする。
	// 差分を閉じたときに差分前のプレビュー表示へ戻さないよう、覚えていた表示も捨てる。
	useEffect(() => {
		if (!isCodeEditable) return;
		setViewMode("source");
		viewModeBeforeDiffRef.current = null;
	}, [isCodeEditable]);

	if (!previewFile) {
		return (
			<div
				style={{
					display: "flex",
					height: "100%",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<p style={{ fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
					ファイルを選択するとプレビューが表示されます
				</p>
			</div>
		);
	}

	const handleViewModeChange = (mode: "preview" | "source") => {
		if (mode === "preview") {
			setPreviewSource(editorRef.current?.getContent() ?? null);
		}
		setViewMode(mode);
	};

	return (
		<div
			data-testid="preview-content"
			style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}
		>
			<FileHeaderBar
				path={previewFile.path}
				onNavigateToDirectory={readOnly ? ignoreNavigation : navigateToEnvironmentFilesTreeNode}
				dirty={dirty}
				saving={saving}
				showMenu={editable && !readOnly && !embeddedDiffOpen}
				viewMode={isToggleEditable && !embeddedDiffOpen ? viewMode : undefined}
				onViewModeChange={handleViewModeChange}
				showWordWrapToggle={!embeddedDiffOpen && editable && sourceVisible}
				onSave={() => void editorRef.current?.save()}
				onDiscardRequest={() => setConfirmDiscard(true)}
			/>
			{externalChangeBefore !== null && !embeddedDiffOpen && (
				<div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-b border-line-1 bg-surface-2 px-3 py-1.5">
					<span className="mr-auto text-caption text-fg-2">
						エージェントがこのファイルを変更しました。
					</span>
					<span className="flex shrink-0">
						<ConfirmButton
							label="差分を見る"
							variant="ghost"
							size="sm"
							loading={false}
							onClick={() => {
								editorRef.current?.showExternalChangeDiff();
								enterDiffView();
							}}
							data-testid="file-external-change-show-diff"
						/>
					</span>
					<IconButton
						icon="x"
						iconSize={14}
						size={20}
						onClick={() => editorRef.current?.dismissExternalChangeNotification()}
						data-testid="file-external-change-dismiss"
						style={{ flexShrink: 0 }}
					/>
				</div>
			)}
			<div style={{ flex: 1, minHeight: 0 }}>
				{!previewUrls || !environmentId ? (
					<PreviewSkeleton />
				) : (
					<>
						{isToggleEditable && (
							<div
								style={{
									display: viewMode === "preview" ? "block" : "none",
									height: "100%",
									overflow: "auto",
									overscrollBehavior: "none",
								}}
							>
								{isMarkdownEditable ? (
									<MarkdownPreview
										url={previewUrls.contentUrl}
										path={previewFile.path}
										source={dirty ? (previewSource ?? undefined) : undefined}
									/>
								) : isSvgEditable ? (
									<SvgPreview
										url={previewUrls.contentUrl}
										filename={filename}
										source={dirty ? (previewSource ?? undefined) : undefined}
									/>
								) : isHtmlEditable ? (
									<HtmlPreview
										environmentId={environmentId}
										path={previewFile.path}
										version={previewFile.updatedAt}
										filename={filename}
										source={dirty ? (previewSource ?? undefined) : undefined}
									/>
								) : (
									<CsvPreview
										url={previewUrls.contentUrl}
										delimiter={csvDelimiter}
										source={dirty ? (previewSource ?? undefined) : undefined}
									/>
								)}
							</div>
						)}
						{/* 種別が変わるリネームで再マウントして未保存の編集を失わないよう、常に同じ位置に置く。 */}
						{editable && (
							<div style={{ display: sourceVisible ? "block" : "none", height: "100%" }}>
								<Suspense fallback={<PreviewSkeleton />}>
									<FileEditor
										ref={editorRef}
										environmentId={environmentId}
										fileId={previewFile.id}
										path={previewFile.path}
										filename={filename}
										updatedAt={previewFile.updatedAt}
										version={previewFile.version}
										active={active && sourceVisible}
										onDirtyChange={(d) => setFileDirty(previewFile.id, d)}
										onSavingChange={setSaving}
										onDiffOpenChange={handleDiffOpenChange}
										onExternalChangeNoticeChange={setExternalChangeBefore}
										readOnly={readOnly}
									/>
								</Suspense>
							</div>
						)}
						{!editable && (
							<div style={{ height: "100%", overflow: "auto", overscrollBehavior: "none" }}>
								{(previewType === "image" || previewType === "svg") && (
									<ImagePreview url={previewUrls.contentUrl} filename={filename} />
								)}
								{previewType === "video" && <VideoPreview url={previewUrls.contentUrl} />}
								{previewType === "pdf" && (
									<PdfPreview url={previewUrls.contentUrl} filename={filename} />
								)}
								{previewType === "docx" &&
									(withinDocxSizeLimit ? (
										<Suspense fallback={<PreviewSkeleton />}>
											<DocxPreview url={previewUrls.contentUrl} />
										</Suspense>
									) : (
										<UnsupportedFilePreview
											downloadUrl={previewUrls.downloadUrl}
											filename={filename}
											message="ファイルが大きすぎるためプレビューを表示できません"
										/>
									))}
								{previewType === "excel" &&
									(withinTableSizeLimit ? (
										<Suspense fallback={<PreviewSkeleton />}>
											<ExcelPreview url={previewUrls.contentUrl} />
										</Suspense>
									) : (
										<UnsupportedFilePreview
											downloadUrl={previewUrls.downloadUrl}
											filename={filename}
											message="ファイルが大きすぎるためプレビューを表示できません"
										/>
									))}
								{previewType === "csv" &&
									(withinTableSizeLimit ? (
										<CsvPreview url={previewUrls.contentUrl} delimiter={csvDelimiter} />
									) : (
										<UnsupportedFilePreview
											downloadUrl={previewUrls.downloadUrl}
											filename={filename}
											message="ファイルが大きすぎるためプレビューを表示できません"
										/>
									))}
								{previewType === "markdown" && (
									<MarkdownPreview
										url={previewUrls.contentUrl}
										path={previewFile.path}
										knowledgeArticle={isKnowledgeArticleFileId(previewFile.id)}
									/>
								)}
								{previewType === "html" && !isSourceViewer && (
									<HtmlPreview
										environmentId={environmentId}
										path={previewFile.path}
										version={previewFile.updatedAt}
										filename={filename}
									/>
								)}
								{isSourceViewer && (
									<UnsupportedFilePreview
										downloadUrl={previewUrls.downloadUrl}
										filename={filename}
										message="ファイルが大きすぎるためプレビューを表示できません"
									/>
								)}
							</div>
						)}
					</>
				)}
			</div>
			<ConfirmDialog
				open={confirmDiscard}
				title="編集を破棄"
				message="編集内容を破棄して保存済みの内容に戻します。よろしいですか？"
				confirmLabel="破棄する"
				onConfirm={async () => {
					editorRef.current?.discard();
					setConfirmDiscard(false);
				}}
				onCancel={() => setConfirmDiscard(false)}
			/>
		</div>
	);
}
